import { SessionWriter } from "@musejs/recording";
import { ATHENA_CONTROL_CHARACTERISTIC, ATHENA_DATA_CHARACTERISTICS, ATHENA_UNIVERSAL_CHARACTERISTIC, BoundedStream, LEGACY_DATA_CHARACTERISTICS, LegacyAssembler, MUSE_COMMANDS, MuseError, SampleAssembler, decodeAthenaNotification, encodeCommand, isAthenaDataCharacteristic, isPresetToken, presetCommand, profileForPreset, windowedClock } from "@musejs/core";
//#region src/recording.ts
var Recording = class {
	#writer;
	#stopped = false;
	#describe = null;
	#observed = /* @__PURE__ */ new Set();
	constructor(writer) {
		this.#writer = writer;
	}
	get frameCount() {
		return this.#writer.frameCount;
	}
	get stopped() {
		return this.#stopped;
	}
	/**
	* Marks a moment.
	*
	* The marker carries an explicit uncertainty, because a timestamp taken on the
	* host has no hardware relationship to the headband's clock. Anything that
	* needs millisecond alignment needs a hardware trigger, not this.
	*/
	mark(label, data) {
		return this.#writer.mark(label, data);
	}
	/**
	* Serialises the session. The recording cannot be used afterwards.
	*
	* The device is described now, from what is known at the end — the preset
	* actually applied, the firmware reported — not from what was known when the
	* recording was armed. If serialising fails the recording stays open.
	*/
	finish() {
		if (this.#describe !== null) this.describe(this.#describe());
		this.#writer.describePreset({ observedSignals: [...this.#observed].toSorted() });
		const bytes = this.#writer.finish();
		this.#stopped = true;
		return bytes;
	}
	/** @internal — the device supplies its description lazily, for finish(). */
	describeWith(describe) {
		this.#describe = describe;
	}
	/** @internal — called for every chunk assembled while recording. */
	observe(signal) {
		if (!this.#stopped) this.#observed.add(signal);
	}
	/** @internal — called by the device for every notification. */
	addFrame(characteristic, payload, arrivalTime) {
		if (this.#stopped) return;
		this.#writer.addFrame(characteristic, payload, arrivalTime);
	}
	/** @internal */
	describe(device) {
		this.#writer.describeDevice({
			id: device.id,
			name: device.name,
			model: device.protocol === "universal" ? "MS-03" : null,
			protocol: device.protocol,
			firmware: device.firmware,
			mtu: device.mtu
		});
		if (device.preset !== null) this.#writer.describePreset({ requested: device.preset });
	}
};
function createRecording(transport, options = {}) {
	return new Recording(new SessionWriter({
		transport,
		...options.subject === void 0 ? {} : { subject: options.subject },
		...options.session === void 0 ? {} : { session: options.session },
		...options.task === void 0 ? {} : { task: options.task },
		...options.notes === void 0 ? {} : { notes: options.notes },
		...options.extra === void 0 ? {} : { extra: options.extra }
	}));
}
//#endregion
//#region src/device.ts
/** Most anomalies kept; a persistent fault stays visible without exhausting memory. */
const MAX_ANOMALIES = 100;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/**
* A connected Muse headband.
*
* Signals arrive two ways: {@link on} for a synchronous callback per chunk, and
* `streams.<signal>.chunks()` for an async iterable with a bounded buffer. The
* two exist side by side so a recorder is never slowed by a sluggish display.
*/
var MuseDevice = class {
	#assembler;
	#legacy = null;
	#protocol = "universal";
	#experimentalLegacy;
	#createClock;
	#streams = /* @__PURE__ */ new Map();
	#unsubscribes = [];
	#capacity;
	#policy;
	#anomalies = [];
	#status = "connecting";
	#info = {
		firmware: null,
		serialNumber: null,
		macAddress: null,
		name: null,
		batteryPercent: null,
		presetDecimal: null,
		hardware: null,
		bootloader: null,
		buildNumber: null,
		protocolVersion: null,
		raw: {}
	};
	/** Resolvers waiting on the next control reply that carries a firmware field. */
	#infoWaiters = /* @__PURE__ */ new Set();
	#controlBuffer = "";
	/** Streaming, so a multi-byte character split across notifications survives. */
	#controlDecoder = new TextDecoder();
	#appliedPreset = null;
	#recordings = /* @__PURE__ */ new Set();
	#reconnect;
	#transport;
	#connection;
	#reconnecting = false;
	#closedByCaller = false;
	#reconnects = 0;
	#failure = null;
	#handshake;
	#commandDelayMs;
	#onListenerError;
	constructor(connection, options = {}, transport) {
		this.#connection = connection;
		this.#transport = transport ?? null;
		const reconnect = options.autoReconnect;
		this.#reconnect = reconnect === false || transport === void 0 ? null : {
			attempts: reconnect?.attempts ?? 5,
			initialDelayMs: reconnect?.initialDelayMs ?? 500,
			maxDelayMs: reconnect?.maxDelayMs ?? 8e3
		};
		this.#capacity = options.streamCapacity ?? 256;
		this.#policy = options.onOverflow ?? "dropOldest";
		this.#handshake = options.handshake ?? true;
		this.#commandDelayMs = options.commandDelayMs ?? 250;
		this.#experimentalLegacy = options.experimentalLegacy ?? false;
		this.#createClock = options.createClock ?? (() => windowedClock());
		this.#onListenerError = options.onListenerError;
		this.#assembler = new SampleAssembler({ createClock: this.#createClock });
		this.#watchForDrop();
	}
	/** How many times the link has been re-established. */
	get reconnects() {
		return this.#reconnects;
	}
	#watchForDrop() {
		const connection = this.#connection;
		connection.closed.then((reason) => {
			if (connection !== this.#connection) return reason;
			if (this.#closedByCaller || reason.kind === "requested" || reason.kind === "ended") {
				this.#finish("disconnected");
				return reason;
			}
			const terminal = reason.kind === "error" && reason.error !== void 0 && !reason.error.recoverable;
			if (this.#reconnect === null || terminal) {
				this.#finish(reason.kind === "error" ? "error" : "disconnected", reason.error);
				return reason;
			}
			this.#attemptReconnect();
			return reason;
		});
	}
	/**
	* Reopens the link, backing off between attempts.
	*
	* Streams are deliberately left open: a drop is a gap in the data, not the end
	* of the session, and ending them would force every consumer to rebuild its
	* pipeline over a few hundred milliseconds of absence.
	*/
	async #attemptReconnect() {
		if (this.#reconnecting || this.#transport === null || this.#reconnect === null) return;
		this.#reconnecting = true;
		const wasStreaming = this.#status === "streaming";
		this.#status = "connecting";
		for (const off of this.#unsubscribes.splice(0)) off();
		let delay = this.#reconnect.initialDelayMs;
		for (let attempt = 1; attempt <= this.#reconnect.attempts; attempt++) {
			if (this.#closedByCaller) break;
			await sleep(delay);
			delay = Math.min(delay * 2, this.#reconnect.maxDelayMs);
			if (this.#closedByCaller) break;
			let connection;
			try {
				connection = await this.#transport.open(this.#connection.id);
				if (this.#closedByCaller) {
					await connection.close();
					break;
				}
				this.#connection = connection;
				this.#assembler.reset();
				this.#controlBuffer = "";
				this.#controlDecoder = new TextDecoder();
				await this.open();
				if (wasStreaming && this.#appliedPreset !== null) await this.start({ preset: this.#appliedPreset });
				if (this.#closedByCaller) {
					await connection.close();
					break;
				}
				this.#reconnects++;
				this.#reconnecting = false;
				this.#watchForDrop();
				return;
			} catch {
				for (const off of this.#unsubscribes.splice(0)) off();
				if (connection !== void 0 && connection !== this.#connection) await connection.close().catch(() => {});
			}
		}
		this.#reconnecting = false;
		this.#finish(this.#closedByCaller ? "disconnected" : "error");
	}
	#finish(status, error) {
		this.#status = status;
		if (error !== void 0) this.#failure = error;
		for (const stream of this.#streams.values()) if (error === void 0) stream.end();
		else stream.fail(error);
	}
	/** Why the session ended, when it ended in an error the transport reported. */
	get failure() {
		return this.#failure;
	}
	get id() {
		return this.#connection.id;
	}
	get status() {
		return this.#status;
	}
	get info() {
		return this.#info;
	}
	/**
	* Largest notification payload the link carries (ATT MTU − 3), or `null`
	* until it is known. See {@link Connection.mtu} for what each transport reports.
	*/
	get mtu() {
		return this.#connection.mtu;
	}
	/**
	* Which wire protocol this device turned out to speak.
	*
	* Determined from the GATT table on `open()`, never from the device name.
	*/
	get protocol() {
		return this.#protocol;
	}
	/** The preset actually applied, once observed in a status reply. */
	get preset() {
		return this.#appliedPreset;
	}
	/**
	* What the applied preset is known to produce, or `undefined` for a token
	* outside the registry.
	*
	* The registry lists only presets whose behaviour has been observed here, and
	* the device accepts more than that. `undefined` means the token was sent and
	* the stream is whatever the device chose to send — read `signals` from the
	* data rather than assuming. A profile with `verified: false` is an
	* expectation, not a measurement.
	*/
	get presetProfile() {
		return this.#appliedPreset === null ? void 0 : profileForPreset(this.#appliedPreset);
	}
	/**
	* Structural problems met while decoding.
	*
	* Should stay empty. A non-empty list means the firmware is emitting something
	* this library does not understand, which is worth reporting rather than
	* discarding.
	*/
	get anomalies() {
		return this.#anomalies;
	}
	/** Per-sensor clock fit statistics, keyed by subpacket tag. */
	clockQuality() {
		return this.#assembler.clockQuality();
	}
	get streams() {
		return {
			eeg: this.#streamFor("eeg"),
			imu: this.#streamFor("imu"),
			optics: this.#streamFor("optics"),
			battery: this.#streamFor("battery"),
			drlref: this.#streamFor("drlref"),
			ppg: this.#streamFor("ppg"),
			telemetry: this.#streamFor("telemetry")
		};
	}
	/** Registers a synchronous listener for one signal. */
	on(signal, listener) {
		return this.#streamFor(signal).subscribe(listener);
	}
	/** Subscribes to the transport and runs the initialisation handshake. */
	async open() {
		this.#status = "handshaking";
		for (const off of this.#unsubscribes.splice(0)) off();
		this.#unsubscribes.push(await this.#connection.subscribe(ATHENA_CONTROL_CHARACTERISTIC, (data, arrivalTime) => {
			this.#recordFrame(ATHENA_CONTROL_CHARACTERISTIC, data, arrivalTime);
			this.#absorbControl(data);
		}));
		let universal = false;
		for (const uuid of ATHENA_DATA_CHARACTERISTICS) try {
			this.#unsubscribes.push(await this.#connection.subscribe(uuid, (data, arrivalTime) => {
				this.#absorbData(uuid, data, arrivalTime);
			}));
			if (uuid === ATHENA_UNIVERSAL_CHARACTERISTIC) universal = true;
		} catch (error) {
			if (!MuseError.is(error) || error.code !== "UNSUPPORTED_DEVICE") throw error;
		}
		if (!universal) {
			if (!this.#experimentalLegacy) throw new MuseError("UNSUPPORTED_DEVICE", "This device does not expose the Athena data characteristic. It is most likely a pre-Athena Muse, whose support is experimental and untested against hardware — pass `experimentalLegacy: true` to try it anyway.", { details: { characteristic: ATHENA_UNIVERSAL_CHARACTERISTIC } });
			this.#protocol = "legacy";
			this.#legacy = new LegacyAssembler({ createClock: this.#createClock });
			for (const uuid of LEGACY_DATA_CHARACTERISTICS) try {
				this.#unsubscribes.push(await this.#connection.subscribe(uuid, (data, arrivalTime) => {
					this.#absorbData(uuid, data, arrivalTime);
				}));
			} catch (error) {
				if (!MuseError.is(error) || error.code !== "UNSUPPORTED_DEVICE") throw error;
			}
		}
		if (this.#handshake) {
			await this.#send(MUSE_COMMANDS.version);
			await this.#send(MUSE_COMMANDS.status);
			await this.#send(MUSE_COMMANDS.halt);
		}
		this.#status = "ready";
	}
	/**
	* Applies a preset and starts streaming.
	*
	* `dc001` is sent twice on purpose. Both published implementations agree the
	* device ignores the first one, and hardware confirms it.
	*/
	async start(options = {}) {
		const preset = options.preset ?? "p1021";
		if (!isPresetToken(preset)) throw new MuseError("PRESET_REJECTED", `"${preset}" is not shaped like a preset token (expected "p" followed by 2-4 hex digits)`, { details: { preset } });
		if (this.#handshake) {
			await this.#send(presetCommand(preset));
			await this.#send(MUSE_COMMANDS.status);
			await this.#send(MUSE_COMMANDS.startStreaming, this.#pace(60));
			await this.#send(MUSE_COMMANDS.startStreaming, this.#pace(120));
			await this.#send(MUSE_COMMANDS.lowLatency, this.#pace(300));
			await this.#send(MUSE_COMMANDS.status);
		}
		this.#appliedPreset = preset;
		this.#status = "streaming";
	}
	/** Halts streaming. The connection stays open. */
	async stop() {
		if (this.#handshake) await this.#send(MUSE_COMMANDS.halt, this.#pace(200));
		if (this.#status === "streaming") this.#status = "ready";
	}
	/**
	* Halts the stream without tearing down the configuration.
	*
	* Distinct from `stop()`: `pause()`/`resume()` are a pair, and resuming does
	* not reapply the preset, because the device still holds it. `stop()` is the
	* end of a streaming session.
	*/
	async pause() {
		await this.#send(MUSE_COMMANDS.halt, this.#pace(200));
		if (this.#status === "streaming") this.#status = "ready";
	}
	/** Resumes a paused stream, reusing the preset the device already holds. */
	async resume() {
		await this.#send(MUSE_COMMANDS.resume, this.#pace(60));
		this.#status = "streaming";
	}
	/**
	* Asks the device to identify itself, and waits for the reply.
	*
	* The control channel is not request/response — replies arrive as unsolicited
	* notifications — so this sends the version command and resolves on the next
	* reply that actually carries a firmware field. Everything learned along the
	* way is merged into `info` regardless of who asked.
	*
	* @throws {MuseError} `HANDSHAKE_FAILED` if nothing answers in time.
	*/
	async deviceInfo({ timeoutMs = 3e3 } = {}) {
		if (this.#info.firmware !== null) return this.#info;
		let timer;
		let waiter;
		const answered = new Promise((resolve, reject) => {
			waiter = (info) => {
				clearTimeout(timer);
				resolve(info);
			};
			timer = setTimeout(() => {
				if (waiter !== void 0) this.#infoWaiters.delete(waiter);
				reject(new MuseError("HANDSHAKE_FAILED", `No device-information reply within ${timeoutMs} ms`, { details: { timeoutMs } }));
			}, timeoutMs);
			timer.unref?.();
			this.#infoWaiters.add(waiter);
		});
		try {
			await this.#send(MUSE_COMMANDS.version, 0);
		} catch (error) {
			clearTimeout(timer);
			if (waiter !== void 0) this.#infoWaiters.delete(waiter);
			answered.catch(() => {});
			throw error;
		}
		return answered;
	}
	async disconnect() {
		this.#closedByCaller = true;
		for (const off of this.#unsubscribes) off();
		this.#unsubscribes.length = 0;
		for (const stream of this.#streams.values()) stream.end();
		await this.#connection.close();
		this.#status = "disconnected";
	}
	/**
	* Starts recording raw notifications.
	*
	* Recording is independent of consumption: it takes every frame as it arrives,
	* so a slow consumer of `streams` cannot cost the recording data. Several
	* recordings may run at once.
	*/
	record(options = {}) {
		const recording = createRecording(this.#transport?.name ?? "unknown", options);
		recording.describeWith(() => ({
			id: this.id,
			name: this.#info.name,
			firmware: this.#info.firmware,
			mtu: this.mtu,
			preset: this.#appliedPreset,
			protocol: this.#protocol
		}));
		this.#recordings.add(recording);
		return recording;
	}
	/** Sends a raw control command. Fire and forget: the device does not reply to most. */
	async send(command) {
		await this.#send(command, 0);
	}
	/**
	* Scales a per-command pause.
	*
	* The start sequence needs shorter pauses at some steps than the default, but
	* a caller who asked for no pacing at all — a test, a replay — must get none.
	*/
	#pace(milliseconds) {
		return this.#commandDelayMs === 0 ? 0 : milliseconds;
	}
	async #send(command, waitMs = this.#commandDelayMs) {
		await this.#connection.write(ATHENA_CONTROL_CHARACTERISTIC, encodeCommand(command));
		if (waitMs > 0) await sleep(waitMs);
	}
	#streamFor(signal) {
		let stream = this.#streams.get(signal);
		if (stream === void 0) {
			stream = new BoundedStream({
				capacity: this.#capacity,
				onOverflow: this.#policy,
				onListenerError: (error) => this.#listenerFailed(error, signal)
			});
			this.#streams.set(signal, stream);
		}
		return stream;
	}
	#absorbData(uuid, data, arrivalTime) {
		this.#recordFrame(uuid, data, arrivalTime);
		if (this.#protocol === "legacy") {
			try {
				for (const chunk of this.#legacy?.accept(uuid, data, arrivalTime) ?? []) this.#emit(chunk);
			} catch (error) {
				if (MuseError.is(error)) this.#anomaly({
					kind: "legacy-decode-failed",
					characteristic: uuid,
					message: error.message
				});
			}
			return;
		}
		if (!isAthenaDataCharacteristic(uuid)) return;
		const result = decodeAthenaNotification(data);
		for (const anomaly of result.anomalies) this.#anomaly(anomaly);
		for (const packet of result.packets) {
			let chunks;
			try {
				chunks = this.#assembler.accept(packet, arrivalTime);
			} catch (error) {
				this.#anomaly({
					kind: "assembly-failed",
					message: error instanceof Error ? error.message : String(error)
				});
				continue;
			}
			for (const chunk of chunks) this.#emit(chunk);
		}
	}
	#emit(chunk) {
		for (const recording of this.#recordings) recording.observe(chunk.signal);
		this.#streamFor(chunk.signal).push(chunk);
	}
	/** Keeps a bounded record: visible without letting a pathological stream exhaust memory. */
	#anomaly(anomaly) {
		if (this.#anomalies.length < MAX_ANOMALIES) this.#anomalies.push(anomaly);
	}
	#listenerFailed(error, signal) {
		const message = error instanceof Error ? error.message : String(error);
		this.#anomaly({
			kind: "listener-error",
			signal,
			message
		});
		if (this.#onListenerError !== void 0) this.#onListenerError(error, signal);
		else console.error(`@musejs/muse: an on("${signal}") listener threw; other consumers were unaffected`, error);
	}
	/** Hands a raw notification to every recording still running. */
	#recordFrame(characteristic, payload, arrivalTime) {
		for (const recording of this.#recordings) if (recording.stopped) this.#recordings.delete(recording);
		else recording.addFrame(characteristic, payload, arrivalTime);
	}
	/**
	* Reassembles control replies.
	*
	* They arrive as length-prefixed fragments spread over several notifications,
	* so text is accumulated until a closing brace completes a JSON object.
	*/
	#absorbControl(data) {
		const end = Math.min(data.length, 1 + (data[0] ?? 0));
		this.#controlBuffer += this.#controlDecoder.decode(data.subarray(1, end), { stream: true });
		let close = this.#controlBuffer.indexOf("}");
		while (close >= 0) {
			const open = this.#controlBuffer.lastIndexOf("{", close);
			const skipped = this.#controlBuffer.slice(0, open < 0 ? close + 1 : open).trim();
			if (skipped.length > 0) this.#anomaly({
				kind: "control-reply-discarded",
				message: `Unmatched control text: ${skipped.slice(0, 80)}`
			});
			if (open >= 0) {
				const candidate = this.#controlBuffer.slice(open, close + 1);
				try {
					this.#applyStatus(JSON.parse(candidate));
				} catch {
					this.#anomaly({
						kind: "control-reply-discarded",
						message: `Unparseable control reply: ${candidate.slice(0, 80)}`
					});
				}
			}
			this.#controlBuffer = this.#controlBuffer.slice(close + 1);
			close = this.#controlBuffer.indexOf("}");
		}
		if (this.#controlBuffer.length > 4096) {
			this.#anomaly({
				kind: "control-reply-discarded",
				message: "Control buffer overflowed without a closing brace"
			});
			this.#controlBuffer = "";
		}
	}
	#applyStatus(status) {
		const str = (key) => typeof status[key] === "string" ? status[key] : null;
		const num = (key) => typeof status[key] === "number" ? status[key] : null;
		const raw = { ...this.#info.raw };
		for (const [key, value] of Object.entries(status)) if (typeof value === "string" || typeof value === "number") raw[key] = value;
		this.#info = {
			firmware: str("fw") ?? this.#info.firmware,
			serialNumber: str("sn") ?? this.#info.serialNumber,
			macAddress: str("ma") ?? this.#info.macAddress,
			name: str("hn") ?? this.#info.name,
			batteryPercent: num("bp") ?? this.#info.batteryPercent,
			presetDecimal: num("ps") ?? this.#info.presetDecimal,
			hardware: str("hw") ?? this.#info.hardware,
			bootloader: str("bl") ?? this.#info.bootloader,
			buildNumber: num("bn") ?? this.#info.buildNumber,
			protocolVersion: num("pv") ?? this.#info.protocolVersion,
			raw
		};
		if (str("fw") !== null && this.#infoWaiters.size > 0) {
			const waiters = [...this.#infoWaiters];
			this.#infoWaiters.clear();
			for (const resolve of waiters) resolve(this.#info);
		}
	}
};
/** Lists Muse headbands the transport can see. */
async function* scan(transport, options = {}) {
	for await (const device of transport.scan(options)) yield {
		id: device.id,
		name: device.name,
		rssi: device.rssi
	};
}
/**
* Connects to a headband and completes the handshake.
*
* With no `deviceId`, takes the first device the transport reports.
*/
async function connect(transport, options = {}) {
	let deviceId = options.deviceId;
	if (deviceId === void 0) {
		const prefix = options.namePrefix === void 0 ? "Muse" : options.namePrefix;
		const rejected = [];
		for await (const found of transport.scan({
			...options.signal === void 0 ? {} : { signal: options.signal },
			timeoutMs: options.scanTimeoutMs ?? 15e3
		})) {
			if (prefix !== null && found.name?.startsWith(prefix) !== true) {
				rejected.push(found.name ?? found.id);
				continue;
			}
			deviceId = found.id;
			break;
		}
		if (deviceId === void 0) throw new MuseError("DEVICE_NOT_FOUND", prefix === null ? "No device was discovered" : `No device named "${prefix}…" was discovered. Is the headband switched on and not paired elsewhere?`, { details: {
			namePrefix: prefix,
			sawInstead: rejected.slice(0, 8)
		} });
	}
	const connection = options.signal === void 0 ? await transport.open(deviceId) : await transport.open(deviceId, { signal: options.signal });
	const device = new MuseDevice(connection, options, transport);
	try {
		await device.open();
	} catch (error) {
		await connection.close();
		throw MuseError.wrap(error, "HANDSHAKE_FAILED", `Handshake with ${deviceId} failed`);
	}
	return device;
}
//#endregion
export { MuseDevice, Recording, connect, createRecording, scan };

//# sourceMappingURL=index.js.map